import React, { useContext, useCallback } from "react";
import "../CSS/App.css";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  FaTrash,
  FaCopy,
  FaPlus,
  FaShareAlt,
  FaEdit,
  FaStar,
  FaRegStar,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import {
  doc,
  getFirestore,
  deleteDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { FaBars, FaHome } from "react-icons/fa";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { ItemContext } from "./ItemContext";
import SideModal from "./SideModal";
import ProfileMenu from "./ProfileMenu";
import NotificationBell from "./NotificationBell";
import { Share } from "@capacitor/share";
import GoogleMapButton from "./GoogleMapButton";
import { getCurrentPosition, findNearbyAddress } from "../utils/proximity";

Modal.setAppElement("#root"); // Important for accessibility

const Main = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate(); // Navigation hook
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [user, setUser] = useState(null);
  const auth = getAuth();
  const db = getFirestore();
  const { setSelectedItem } = useContext(ItemContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [userCategories, setUserCategories] = useState([]);
  const [nearby, setNearby] = useState(null);
  // Which address card is expanded to show its action bar (tap to toggle).
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  // Snap the addresses card to a whole number of rows: if the last row would be
  // clipped, hide it and end the card's border at the last row that fully fits;
  // the hidden rows are revealed by scrolling the card. Measured from the live
  // layout (below) rather than a hard-coded row height.
  const listRegionRef = useRef(null);
  const cardRef = useRef(null);
  const rowRef = useRef(null);
  const bottomRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(null);

  // Category chips: fixed (no horizontal scroll). They wrap to two rows; if
  // there are more than fit, they page. catPages is the categories grouped into
  // pages of two rows each, measured from the live layout (below).
  const catMeasureRef = useRef(null);
  const [catPages, setCatPages] = useState([]);
  const [catPage, setCatPage] = useState(0);

  const shareContent = async (address) => {
    try {
      const result = await Share.share({
        title: "Share via",
        text: address,
        dialogTitle: "Select an app to share",
      });
      console.log("Shared successfully:", result);
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };
  const getUserAddresses = useCallback(
    async (userId) => {
      const addressesRef = collection(db, "users", userId, "addresses");
      const querySnapshot = await getDocs(addressesRef);

      let addresses = [];
      querySnapshot.forEach((doc) => {
        addresses.push({ id: doc.id, ...doc.data() });
      });
      return addresses;
    },
    [db],
  );

  const toggleFavorite = async (item) => {
    const nextValue = !item.isFavorite;
    // Optimistic update so the star flips instantly instead of waiting
    // on the round-trip.
    setAddresses((prevData) =>
      prevData.map((a) =>
        a.id === item.id ? { ...a, isFavorite: nextValue } : a,
      ),
    );
    try {
      await updateDoc(doc(db, "users", user.uid, "addresses", item.id), {
        isFavorite: nextValue,
      });
    } catch (error) {
      console.error("Error toggling favorite:", error);
      // Roll back on failure.
      setAddresses((prevData) =>
        prevData.map((a) =>
          a.id === item.id ? { ...a, isFavorite: !nextValue } : a,
        ),
      );
    }
  };

  const handleNavigate = async (item) => {
    const now = Date.now();
    setAddresses((prevData) =>
      prevData.map((a) =>
        a.id === item.id
          ? {
              ...a,
              lastNavigatedAt: now,
              navigationCount: (a.navigationCount || 0) + 1,
            }
          : a,
      ),
    );
    try {
      await updateDoc(doc(db, "users", user.uid, "addresses", item.id), {
        lastNavigatedAt: now,
        navigationCount: (item.navigationCount || 0) + 1,
      });
    } catch (error) {
      // Non-critical (just powers the "Recently used" row) - log and move on.
      console.error("Error recording navigation:", error);
    }
  };

  const handleExport = async () => {
    if (addresses.length === 0) return;
    const lines = addresses.map((item) => {
      const category = item.category ? ` [${item.category}]` : "";
      return `${item.Title}${category}\n${item.Address}`;
    });
    try {
      await Share.share({
        title: "My iBlink Addresses",
        text: lines.join("\n\n"),
        dialogTitle: "Export addresses",
      });
    } catch (error) {
      console.error("Error exporting addresses:", error);
    }
  };

  const deleteAddress = async () => {
    console.log("inside the hanldle delete: ", !itemToDelete);
    if (itemToDelete === null) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "addresses", itemToDelete));
      setAddresses((prevData) =>
        prevData.filter((item) => item.id !== itemToDelete),
      );
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      closeModal();
    }
  };

  // Get the current user state
  useEffect(() => {
    let userDocUnsubscribe = () => {};

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        try {
          // Listen for real-time updates to user data (including categories)
          const userRef = doc(db, "users", user.uid);
          userDocUnsubscribe = onSnapshot(userRef, (userDoc) => {
            if (userDoc.exists()) {
              const userData = userDoc.data();
              // Existing users predating the categories feature have no
              // categories field on their doc yet - fall back to the same
              // defaults ManageCategories seeds new users with.
              setUserCategories([
                "All",
                ...(userData.categories || ["Home", "Work"]),
              ]);
            }
          });

          const addresses = await getUserAddresses(user.uid);
          setAddresses(addresses);
        } catch (fetchError) {
          console.error("Error loading user data:", fetchError);
          setError("Failed to load your addresses. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    });
    // Cleanup the listener when the component unmounts
    return () => {
      authUnsubscribe();
      userDocUnsubscribe();
    };
  }, [navigate, auth, db, getUserAddresses]);

  // Foreground proximity nudge: once addresses are loaded, take a single
  // location reading and, if we're within range of a saved address that has
  // coordinates, offer a one-tap jump to Safety Check-In. Silent on
  // denial/failure - purely additive to the manual flow. A dismissal is
  // remembered per-address for the session so it doesn't nag.
  useEffect(() => {
    if (!addresses.some((a) => typeof a.lat === "number")) return;
    let cancelled = false;
    (async () => {
      const pos = await getCurrentPosition();
      if (cancelled || !pos) return;
      const match = findNearbyAddress(addresses, pos);
      if (!match) return;
      let dismissed = false;
      try {
        dismissed =
          sessionStorage.getItem(`nudge-dismissed-${match.id}`) === "1";
      } catch {
        dismissed = false;
      }
      if (!dismissed) setNearby(match);
    })();
    return () => {
      cancelled = true;
    };
  }, [addresses]);

  // Clear any manual selection whenever the search text changes, so the
  // address box never shows a stale result mid-search. It re-fills only when
  // the filter narrows to exactly one match, or when the user taps a result.
  useEffect(() => {
    setSelectedAddressId(null);
  }, [searchTerm]);

  // Work out the category pages: measure all chips at full width (hidden layer),
  // group them into rows by vertical position, then two rows per page.
  useLayoutEffect(() => {
    const compute = () => {
      const el = catMeasureRef.current;
      if (!el) return;
      const children = Array.from(el.children);
      if (children.length === 0) {
        setCatPages([]);
        return;
      }
      const rowsMap = new Map();
      children.forEach((child, i) => {
        const top = child.offsetTop;
        if (!rowsMap.has(top)) rowsMap.set(top, []);
        rowsMap.get(top).push(userCategories[i]);
      });
      const rows = [...rowsMap.entries()]
        .sort((a, b) => a[0] - b[0])
        .map((e) => e[1]);
      const pages = [];
      for (let i = 0; i < rows.length; i += 2) {
        pages.push([...rows[i], ...(rows[i + 1] || [])]);
      }
      setCatPages(pages);
      setCatPage((p) => Math.min(p, Math.max(0, pages.length - 1)));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCategories, loading]);

  // Cap the addresses card to whole rows. If everything fits, size to content
  // (no scroll). If not, trim the card's height to the last row that fully fits
  // so no row is ever half-shown; overflow lets the user scroll to the rest.
  useLayoutEffect(() => {
    const compute = () => {
      const region = listRegionRef.current;
      const card = cardRef.current;
      const row = rowRef.current;
      if (!region || !card || !row) {
        setCardHeight(null);
        return;
      }
      const rowCS = window.getComputedStyle(row);
      const rowH = row.offsetHeight + (parseFloat(rowCS.marginBottom) || 0);
      if (rowH <= 0) {
        setCardHeight(null);
        return;
      }
      const cardCS = window.getComputedStyle(card);
      const cardPadV =
        (parseFloat(cardCS.paddingTop) || 0) +
        (parseFloat(cardCS.paddingBottom) || 0);
      const cardMarginB = parseFloat(cardCS.marginBottom) || 0;
      const regionCS = window.getComputedStyle(region);
      const regionPadV =
        (parseFloat(regionCS.paddingTop) || 0) +
        (parseFloat(regionCS.paddingBottom) || 0);
      const boxH = bottomRef.current ? bottomRef.current.offsetHeight : 0;
      // Room for the card above the box, inside the region's content box.
      const avail = region.clientHeight - regionPadV - boxH - cardMarginB;
      const contentH = card.scrollHeight; // full list height, whatever the card height
      if (avail <= 0) {
        setCardHeight(null);
        return;
      }
      if (contentH <= avail) {
        setCardHeight(null); // it all fits - size to content
      } else {
        const rows = Math.max(1, Math.floor((avail - cardPadV) / rowH));
        setCardHeight(rows * rowH + cardPadV);
      }
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses.length, searchTerm, selectedCategory, selectedAddressId]);

  const dismissNudge = () => {
    if (nearby) {
      try {
        sessionStorage.setItem(`nudge-dismissed-${nearby.id}`, "1");
      } catch {
        /* sessionStorage unavailable - fine, just won't persist the dismissal */
      }
    }
    setNearby(null);
  };

  const openModal = (id, title) => {
    setItemToDelete(id);
    setModalIsOpen(true);
    setAddressToDelete(title);
    console.log("Opening modal for:", itemToDelete);
  };

  const closeModal = () => {
    setItemToDelete(null);
    setModalIsOpen(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "20%" }}>
        <h2>Loading...</h2>
      </div>
    );
  }
  if (error) return <p>Error: {error}</p>;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      //setCopyStatus("Text copied!");
    } catch (err) {
      console.log("falied to copy with error: ", err);
    }
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    navigate("/edit-item");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Favorites float to the top; alphabetical within each group.
  const sortedItems = [...addresses].sort((a, b) => {
    if (!!a.isFavorite !== !!b.isFavorite) return a.isFavorite ? -1 : 1;
    return a.Title.localeCompare(b.Title);
  });

  // Filter items based on search term and selected category
  const filteredItems = sortedItems.filter((item) => {
    const matchesSearch =
      item.Title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || (item.category || "") === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // The address whose details + actions show in the stable bottom panel.
  // - Search narrowed to exactly one result -> that one auto-fills.
  // - Searching with several results -> stays empty until the user taps one
  //   (the tap sets selectedAddressId; the search-change effect clears it).
  // - Not searching -> the tapped selection, else the first address.
  const isSearching = searchTerm.trim().length > 0;
  const singleFiltered = filteredItems.length === 1 ? filteredItems[0] : null;
  const selectedAddress =
    singleFiltered ||
    addresses.find((a) => a.id === selectedAddressId) ||
    (!isSearching ? filteredItems[0] : null);

  const handleCategoryChange = async (itemId, newCategory) => {
    setAddresses((prevData) =>
      prevData.map((a) =>
        a.id === itemId ? { ...a, category: newCategory } : a,
      ),
    );
    try {
      await updateDoc(doc(db, "users", user.uid, "addresses", itemId), {
        category: newCategory,
      });
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  return (
    <div
      className="App container"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        marginLeft: 0,
        marginRight: 0,
        // True fullscreen: the status bar is hidden entirely (see
        // App.js's StatusBar.hide() call), so there's no system bar to
        // clear anymore - the app can genuinely cover the whole screen,
        // edge to edge, with zero top margin.
        marginTop: 0,
        marginBottom: 0,
        borderRadius: 0,
        paddingTop: "20px",
        paddingLeft: "calc(20px + env(safe-area-inset-left, 0px))",
        paddingRight: "calc(20px + env(safe-area-inset-right, 0px))",
        paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
        boxSizing: "border-box",
        // Bounded to the viewport, list contained inside: the address card
        // fills the space ABOVE the address box and scrolls internally, so the
        // card ends right above the box (small gap) and the box + icons sit
        // just below it - never overlapping, never running off-screen.
        height: "100dvh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "relative",
          flexShrink: 0,
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: "10px",
          marginBottom: "15px",
          marginTop: "30px",
        }}
      >
        <button
          className="menu-button"
          onClick={() => navigate("/")}
          aria-label="Home"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FaHome style={{ display: "block" }} />
        </button>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            className="menu-button"
            onClick={handleOpenModal}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaBars style={{ display: "block" }} />
          </button>
          <SideModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onExport={handleExport}
          />
        </div>
        <input
          type="text"
          autoComplete="off"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search addresses"
          style={{
            flex: 1,
            padding: "10px 15px",
            borderRadius: "20px",
            border: "1px solid var(--border-color, #ccc)",
            backgroundColor: "var(--input-bg, #eef3f8)",
            color: "var(--text-primary, #000)",
          }}
        />

        <NotificationBell />
        <ProfileMenu />
      </div>

      {/* Category filter chips: fixed (no horizontal scroll). They wrap into
          two rows; any extra categories page with the arrows below. */}
      {userCategories.length > 0 && (
        <div style={{ flexShrink: 0, marginTop: "10px" }}>
          {/* Hidden measurement layer: every chip at full width, so the effect
              above can work out how many fit per two-row page. */}
          <div
            ref={catMeasureRef}
            aria-hidden="true"
            style={{
              position: "relative",
              visibility: "hidden",
              height: 0,
              overflow: "hidden",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {userCategories.map((category) => (
              <span
                key={category}
                style={{
                  flexShrink: 0,
                  padding: "6px 14px",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                }}
              >
                {category}
              </span>
            ))}
          </div>

          {/* Visible chips for the current page (up to two rows). */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {(catPages[catPage] || userCategories).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  flexShrink: 0,
                  padding: "6px 14px",
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  backgroundColor:
                    selectedCategory === category
                      ? "var(--chip-bg-active, #007bff)"
                      : "var(--chip-bg, #f0f0f0)",
                  color:
                    selectedCategory === category
                      ? "var(--chip-text-active, #fff)"
                      : "var(--text-primary, #333)",
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Pager - only when categories span more than two rows. */}
          {catPages.length > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "14px",
                marginTop: "8px",
              }}
            >
              <button
                type="button"
                onClick={() => setCatPage((p) => Math.max(0, p - 1))}
                disabled={catPage === 0}
                aria-label="Previous categories"
                style={{
                  background: "none",
                  border: "none",
                  cursor: catPage === 0 ? "default" : "pointer",
                  color:
                    catPage === 0 ? "var(--text-secondary, #ccc)" : "#007bff",
                  padding: "2px 6px",
                }}
              >
                <FaChevronLeft size={14} />
              </button>
              <span
                style={{ fontSize: "12px", color: "var(--text-secondary, #777)" }}
              >
                {catPage + 1} / {catPages.length}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCatPage((p) => Math.min(catPages.length - 1, p + 1))
                }
                disabled={catPage === catPages.length - 1}
                aria-label="More categories"
                style={{
                  background: "none",
                  border: "none",
                  cursor:
                    catPage === catPages.length - 1 ? "default" : "pointer",
                  color:
                    catPage === catPages.length - 1
                      ? "var(--text-secondary, #ccc)"
                      : "#007bff",
                  padding: "2px 6px",
                }}
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Proximity nudge: you appear to be at a saved address - offer a
          one-tap check-in. Dismissible; only shows when device location is
          available and matches a saved address with coordinates. */}
      {nearby && (
        <div
          style={{
            flexShrink: 0,
            marginTop: "10px",
            padding: "12px 14px",
            borderRadius: "12px",
            border: "1px solid var(--border-color, #ddd)",
            backgroundColor: "var(--chip-bg, #f0f0f0)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ flex: 1, fontSize: "14px" }}>
            Looks like you're at <strong>{nearby.Title}</strong>. Check in?
          </div>
          <button
            onClick={() =>
              navigate("/safety", { state: { addressId: nearby.id } })
            }
            style={{
              padding: "6px 14px",
              borderRadius: "16px",
              border: "none",
              cursor: "pointer",
              fontSize: "13px",
              backgroundColor: "#4CAF50",
              color: "white",
              flexShrink: 0,
            }}
          >
            Check in
          </button>
          <button
            onClick={dismissNudge}
            aria-label="Dismiss"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--text-secondary, #777)",
              flexShrink: 0,
            }}
          >
            <AiOutlineClose size={16} />
          </button>
        </div>
      )}

      {/* List region: a scrollable list of titles on top, then a stable
          address box + always-visible action icons below. The bottom panel
          stays put as the selection changes (it never moves). Mirrors the
          Start Event picker. */}
      <div
        ref={listRegionRef}
        style={{
          flex: 1,
          minHeight: 0,
          marginTop: "10px",
          paddingBottom: "64px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {addresses.length === 0 ? (
          <p className="address-item">
            No addresses available. Please add some addresses.
          </p>
        ) : (
          <>
            {/* Addresses card - a rounded card (like the Events card) that
                fills the space above the address box and scrolls inside when
                the list is long, so it ends right above the box with a small
                gap (marginBottom) rather than running off-screen. */}
            <div
              ref={cardRef}
              style={{
                flex: "0 1 auto",
                minHeight: 0,
                height: cardHeight != null ? `${cardHeight}px` : undefined,
                overflowY: "auto",
                marginBottom: "16px",
                padding: "10px",
                borderRadius: "16px",
                border: "1px solid var(--border-color, #ddd)",
                backgroundColor: "var(--item-bg, #fff)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              {filteredItems.map((item, idx) => {
                const isSelected =
                  selectedAddress && selectedAddress.id === item.id;
                return (
                  <button
                    key={item.id}
                    ref={idx === 0 ? rowRef : null}
                    type="button"
                    onClick={() => setSelectedAddressId(item.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      width: "100%",
                      textAlign: "left",
                      padding: "12px 14px",
                      marginBottom: "8px",
                      borderRadius: "10px",
                      border: isSelected
                        ? "2px solid #4CAF50"
                        : "1px solid var(--border-color, #eee)",
                      backgroundColor: isSelected
                        ? "rgba(76, 175, 80, 0.10)"
                        : "var(--chip-bg, #f6f6f6)",
                      color: "var(--text-primary, #1a1a1a)",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: isSelected ? 700 : 500,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {item.isFavorite && <FaStar size={14} color="#f5a623" />}
                      {item.Title}
                    </span>
                    {isSelected && <FaCheck size={15} color="#4CAF50" />}
                  </button>
                );
              })}
            </div>

            {/* Address box + action icons for the selected address, in flow
                just below the card (small gap from the card's marginBottom).
                When searching with no single/selected result, a prompt shows
                instead (else branch). */}
            {selectedAddress ? (
              <div ref={bottomRef} style={{ flexShrink: 0 }}>
                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "10px",
                    backgroundColor: "var(--chip-bg, #f0f0f0)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-secondary, #777)",
                      marginBottom: "2px",
                    }}
                  >
                    Address
                  </div>
                  <div style={{ fontSize: "14px" }}>
                    {selectedAddress.Address}
                  </div>
                  {selectedAddress.notes && (
                    <div
                      style={{
                        fontSize: "13px",
                        fontStyle: "italic",
                        color: "var(--text-secondary, #777)",
                        marginTop: "4px",
                      }}
                    >
                      {selectedAddress.notes}
                    </div>
                  )}
                </div>

                {/* Action icons - always visible under the address box. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginTop: "10px",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                  }}
                >
                  <button
                    onClick={() => toggleFavorite(selectedAddress)}
                    aria-label="Toggle favorite"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: "#f5a623",
                      flexShrink: 0,
                    }}
                  >
                    {selectedAddress.isFavorite ? (
                      <FaStar size={18} />
                    ) : (
                      <FaRegStar size={18} />
                    )}
                  </button>
                  <button
                    onClick={() => copyToClipboard(selectedAddress.Address)}
                    aria-label="Copy address"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: "#007BFF",
                      flexShrink: 0,
                    }}
                  >
                    <FaCopy size={18} />
                  </button>
                  <button
                    onClick={() => handleEditClick(selectedAddress)}
                    aria-label="Edit"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#28A745",
                      cursor: "pointer",
                      padding: "4px",
                      flexShrink: 0,
                    }}
                  >
                    <FaEdit size={20} />
                  </button>
                  <button
                    onClick={() => shareContent(selectedAddress.Address)}
                    aria-label="Share"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      flexShrink: 0,
                    }}
                  >
                    <FaShareAlt size={19} color="#2196F3" />
                  </button>
                  <GoogleMapButton
                    destination={selectedAddress.Address}
                    onNavigate={() => handleNavigate(selectedAddress)}
                  />
                  <button
                    onClick={() =>
                      openModal(selectedAddress.id, selectedAddress.Title)
                    }
                    aria-label="Delete"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#DC3545",
                      padding: "4px",
                      flexShrink: 0,
                    }}
                  >
                    <FaTrash size={18} />
                  </button>
                  {userCategories.length > 0 && (
                    <select
                      value={selectedAddress.category || ""}
                      onChange={(e) =>
                        handleCategoryChange(selectedAddress.id, e.target.value)
                      }
                      style={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        backgroundColor: "var(--chip-bg, #f0f0f0)",
                        color: "var(--text-secondary, #666)",
                        border: "1px solid var(--border-color, transparent)",
                        flexShrink: 0,
                      }}
                    >
                      <option value="" style={{ fontStyle: "italic" }}>
                        No Category
                      </option>
                      {userCategories
                        .filter((c) => c !== "All")
                        .map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </div>
            ) : (
              // Searching with more than one match (or none): keep the box
              // empty and prompt a choice instead of showing a stale address.
              <div
                ref={bottomRef}
                style={{
                  flexShrink: 0,
                  padding: "12px 14px",
                  borderRadius: "10px",
                  backgroundColor: "var(--chip-bg, #f0f0f0)",
                  color: "var(--text-secondary, #777)",
                  fontSize: "14px",
                }}
              >
                {filteredItems.length === 0
                  ? "No matching addresses."
                  : "Select an address from the results above."}
              </div>
            )}
          </>
        )}
      </div>
      {/* Confirmation Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            // react-modal defaults content to position:absolute with
            // top/left/right/bottom:40px, which conflicts with the
            // explicit width/margin below and overrides the overlay's
            // flexbox centering - reset positioning so the overlay's
            // display:flex + justifyContent:center actually centers it.
            position: "relative",
            top: "auto",
            left: "auto",
            right: "auto",
            bottom: "auto",
            width: "50%",
            height: "auto",
            maxHeight: "200px",
            margin: "auto",
            padding: "20px",
            textAlign: "center",
            borderRadius: "10px",
            backgroundColor: "#fff",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "flex-start", // Aligns modal to the top
            justifyContent: "center",
            paddingTop: "100px", // Adjust this to move it lower or higher
          },
        }}
      >
        <h3>Are you sure?</h3>
        <p>
          Do you really want to delete <strong> {addressToDelete} </strong>{" "}
          address ? This action cannot be undone.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={() => deleteAddress(itemToDelete)}
            style={{
              backgroundColor: "red",
              color: "white",
              padding: "10px 15px",
              border: "none",
              cursor: "pointer",
              borderRadius: "5px",
            }}
          >
            Yes, Delete
          </button>
          <button
            onClick={closeModal}
            style={{
              backgroundColor: "gray",
              color: "white",
              padding: "10px 15px",
              border: "none",
              cursor: "pointer",
              borderRadius: "5px",
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
      <button
        onClick={() => navigate("/add")}
        style={{
          marginTop: "20px",
          padding: "10px 15px",
          fontSize: "16px",
          backgroundColor: "#28a745",
          color: "white",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          borderRadius: "5px",
        }}
        className="fab-button"
      >
        <FaPlus size={22} />
      </button>
    </div>
  );
};

export default Main;
